import type { ReactNode } from "react";
import type { MenuRoute } from "./menuTypes";

/**
 * Text search over the admin menu (STIC-15790 follow-up).
 *
 * The own-level menu is 128 rows deep with every group permanently open, so
 * finding a page means scrolling through roughly seven screens. These helpers
 * narrow the pruned tree NavList is handed down to the rows whose label contains
 * what the user typed. All of them are pure: SidebarBody owns the query.
 */

/** A route that may carry the plain-text `title` NavList shows on hover. */
type SearchableRoute = MenuRoute & { title?: string };

/**
 * The text an entry is matched on: its translated label. An entry whose name is
 * a node rather than a string (an icon, a badge) falls back to its `title`, so
 * a label the user cannot read is never what decides a match.
 */
export const menuEntryText = (route: SearchableRoute): string =>
  typeof route.name === "string" ? route.name : route.title ?? "";

export const normalizeMenuQuery = (query: string): string => query.trim().toLowerCase();

/**
 * Keeps the entries whose label contains `query`, case-insensitively.
 *
 * Group names are never matched: a group survives only while one of its
 * children does, so it can never appear as an empty header. Landing rows outside
 * any group are items like any other. An empty query returns the tree as is.
 */
export const filterMenuRoutes = <T extends SearchableRoute>(routes: T[], query: string): T[] => {
  const q = normalizeMenuQuery(query);
  if (!q) return routes;
  return routes.reduce<T[]>((kept, route) => {
    if (route.collapse) {
      const views = filterMenuRoutes((route.views ?? []) as T[], q);
      if (views.length > 0) kept.push({ ...route, views });
      return kept;
    }
    if (menuEntryText(route).toLowerCase().includes(q)) kept.push(route);
    return kept;
  }, []);
};

/**
 * Wraps the matched run of every item label in a <mark>, leaving group names
 * untouched. The plain label moves to `title`, which NavList already prefers
 * for the hover tooltip, so a highlighted row still reads in full on hover.
 */
export const highlightMenuRoutes = <T extends SearchableRoute>(routes: T[], query: string): T[] => {
  const q = normalizeMenuQuery(query);
  if (!q) return routes;
  return routes.map((route) => {
    if (route.collapse) {
      return { ...route, views: highlightMenuRoutes((route.views ?? []) as T[], q) };
    }
    const text = menuEntryText(route);
    const at = text.toLowerCase().indexOf(q);
    if (typeof route.name !== "string" || at < 0) return route;
    const name: ReactNode = (
      <>
        {text.slice(0, at)}
        <mark className="menu-search-hit">{text.slice(at, at + q.length)}</mark>
        {text.slice(at + q.length)}
      </>
    );
    return { ...route, name, title: text };
  });
};

/** The first navigable item of a tree in reading order — what Enter opens. */
export const firstMenuItem = <T extends SearchableRoute>(routes: T[]): T | undefined => {
  for (const route of routes) {
    if (route.collapse) {
      const hit = firstMenuItem((route.views ?? []) as T[]);
      if (hit) return hit;
    } else if (route.to) {
      return route;
    }
  }
  return undefined;
};
